"use client";

import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useToast } from "../components/ui/use-toast";
import api from "../services/api";
import { PasswordRequirementsChecklist } from "../components/PasswordRequirementsChecklist";
import {
  describeMissingRequirements,
  validatePassword,
} from "../utils/passwordPolicy";

const ResetPassword: React.FC = () => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [token, setToken] = useState("");
  const history = useHistory();
  const location = useLocation();
  const { toast } = useToast();

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tokenParam = urlParams.get("token");
    if (tokenParam) {
      setToken(tokenParam);
    } else {
      toast({
        variant: "destructive",
        title: "Errore",
        description: "Token mancante o non valido",
      });
    }
  }, [location, toast]);

  const handleResetPassword = async () => {
    if (!newPassword) {
      toast({
        variant: "destructive",
        title: "Errore",
        description: "Inserisci la nuova password",
      });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({
        variant: "destructive",
        title: "Errore",
        description: "Le password non coincidono",
      });
      return;
    }
    const policyError = describeMissingRequirements(newPassword);
    if (policyError) {
      toast({
        variant: "destructive",
        title: "Errore",
        description: policyError,
      });
      return;
    }

    try {
      await api.post("/reset-password", {
        token: token,
        new_password: newPassword,
      });
      toast({
        variant: "success",
        title: "Password aggiornata",
        description: "Verrai reindirizzato al login",
      });
      setTimeout(() => {
        history.push("/");
      }, 2000);
    } catch (e: any) {
      toast({
        variant: "destructive",
        title: "Errore",
        description:
          e?.response?.status === 400
            ? "Token scaduto o non valido"
            : "Errore nel reset della password",
      });
    }
  };

  const canSubmit =
    !!token &&
    validatePassword(newPassword).valid &&
    newPassword === confirmPassword;

  return (
    <div className="min-h-screen flex items-center justify-center px-5">
      <div className="glass-card w-full max-w-md rounded-2xl p-8 space-y-6">
        <h1 className="gradient-text text-2xl font-bold text-center">
          Reset Password
        </h1>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="newPassword">Nuova Password</Label>
            <Input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Inserisci la nuova password"
              className="backdrop-blur-xl bg-white/5 border border-white/10 shadow-xl"
            />
            <PasswordRequirementsChecklist password={newPassword} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Conferma Password</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Conferma la password"
              className="backdrop-blur-xl bg-white/5 border border-white/10 shadow-xl"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4">
          <Button
            size="lg"
            className="w-full bg-gradient-to-r from-teal-200 to-teal-500 text-white hover:opacity-90"
            onClick={handleResetPassword}
            disabled={!canSubmit}
          >
            Reset Password
          </Button>

          <Button
            variant="ghost"
            className="w-full text-sm text-teal-400 hover:text-teal-200"
            onClick={() => history.push("/")}
          >
            Torna al Login
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
